<template>
  <div class="recommandations-page mandat-page">
    <header class="mandat-page__hero">
      <p class="mandat-page__eyebrow">Mandat</p>
      <h1 class="mandat-page__title">Recommandations</h1>
      <p class="mandat-page__subtitle">
        Suivi des actions mandat pour vos clients : échéances, statuts et matrice RACI.
      </p>
    </header>

    <div class="mandat-page__toolbar">
      <div class="mandat-page__search">
        <span class="mandat-page__search-icon" aria-hidden="true">
          <i class="fas fa-search"></i>
        </span>
        <input type="search" class="form-control" placeholder="Rechercher…" v-model="searchQuery" autocomplete="off">
      </div>
      <button v-if="canEdit" type="button" class="btn btn-primary" @click="createNewRecommendation">
        <i class="fas fa-plus me-2"></i>
        Nouvelle recommandation
      </button>
    </div>

    <div class="mandat-table-wrap">
      <div v-if="errorMessage" class="alert alert-danger mandat-alert">{{ errorMessage }}</div>
      <div v-if="loading" class="mandat-loading text-center">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Chargement…</span>
        </div>
      </div>
      <div v-else-if="recommendations.length === 0" class="alert alert-info mandat-empty">
        Aucune recommandation trouvée.
      </div>
      <div v-else class="table-responsive">
        <table class="table table-hover">
          <thead>
            <tr>
              <th>Titre</th>
              <th>Client</th>
              <th>Date de fin</th>
              <th>Statut</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in paginatedItems" :key="item.id">
              <td>{{ item.titre }}</td>
              <td>{{ item.client?.nom }} {{ item.client?.prenom }}</td>
              <td>{{ formatDate(item.date_echeance) }}</td>
              <td>
                <span :class="getStatusClass(item.statut)">{{ formatStatut(item.statut) }}</span>
              </td>
              <td>
                <button type="button" class="mandat-icon-btn mandat-icon-btn--view me-1" @click="viewItem(item)" title="Voir les détails">
                  <i class="fas fa-eye"></i>
                </button>
                <button v-if="canEdit" type="button" class="mandat-icon-btn mandat-icon-btn--edit me-1" @click="editItem(item)" title="Modifier">
                  <i class="fas fa-edit"></i>
                </button>
                <button v-if="canEdit" type="button" class="mandat-icon-btn mandat-icon-btn--danger" @click="deleteItem(item)" title="Supprimer">
                  <i class="fas fa-trash"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        <div class="mandat-pagination">
          <div class="mandat-pagination__size">
            <label for="page-size-gest" class="mb-0">Afficher</label>
            <select id="page-size-gest" v-model="itemsPerPage" class="form-select form-select-sm">
              <option v-for="n in pageSizes" :key="n" :value="n">{{ n }}</option>
            </select>
            <span>éléments</span>
          </div>
          <div class="mandat-pagination__nav">
            <span>
              {{ (currentPage - 1) * itemsPerPage + 1 }}–{{ Math.min(currentPage * itemsPerPage, filteredItems.length) }}
              sur {{ filteredItems.length }}
            </span>
            <div class="btn-group">
              <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="currentPage === 1" @click="currentPage--">
                Précédent
              </button>
              <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="currentPage >= totalPages" @click="currentPage++">
                Suivant
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
  
  <script>
    import axios from "axios";
  import Swal from 'sweetalert2';
  import { canEditMandat } from '@/utils/permissions';
  import {
    formatMandatDate,
    formatMandatStatut,
    mandatStatutClass,
    MANDAT_PAGE_SIZES,
  } from '@/utils/mandatStatus';
  
  export default {
    name: 'GestionRecommandations',
    data() {
      return {
        searchQuery: '',
        recommendations: [],
        errorMessage: null,
        loading: false,
        currentPage: 1,
        itemsPerPage: 10,
        pageSizes: MANDAT_PAGE_SIZES
      }
    },
    computed: {
      canEdit() {
        return canEditMandat();
      },
      filteredItems() {
        if (!this.searchQuery) return this.recommendations;
        
        const query = this.searchQuery.toLowerCase();
        return this.recommendations.filter(item => 
          String(item.titre || '').toLowerCase().includes(query) ||
          String(item.client?.nom + ' ' + item.client?.prenom || '').toLowerCase().includes(query) ||
          String(item.statut || '').toLowerCase().includes(query)
        );
      },
      totalPages() {
        return Math.ceil(this.filteredItems.length / this.itemsPerPage);
      },
      paginatedItems() {
        const start = (this.currentPage - 1) * this.itemsPerPage;
        const end = start + this.itemsPerPage;
        return this.filteredItems.slice(start, end);
      }
    },
    watch: {
      itemsPerPage() {
        this.currentPage = 1;
      },
      searchQuery() {
        this.currentPage = 1;
      }
    },
    mounted() {
      this.fetchRecommendations();
    },
    methods: {
      async fetchRecommendations() {
        this.loading = true;
        this.errorMessage = null;
        
        try {
          const response = await axios.get("/api/actions-recommandations/", { withCredentials: true, headers: { 'Content-Type': 'application/json' } });
  
          this.recommendations = response.data.recommandations || [];
        } catch (error) {
          console.error('Erreur:', error);
          if (error.response?.status === 401) {
            this.errorMessage = 'Session expirée, veuillez vous reconnecter.';
            this.$router.push('/connexion');
          } else {
            this.errorMessage = error.response?.data?.detail || "Une erreur est survenue lors de la récupération des recommandations.";
          }
        } finally {
          this.loading = false;
        }
      },
      
      getStatusClass(statut) {
        return mandatStatutClass(statut);
      },
      createNewRecommendation() {
        this.$router.push('/actions-mandat/nouvelle-action-recommandation');
      },
      viewItem(item) {
        this.$router.push(`/actions-mandat/actions-recommandations/${item.id}`);
      },
      editItem(item) {
        this.$router.push(`/actions-mandat/actions-recommandations/${item.id}/modifier`);
      },
      async deleteItem(item) {
        try {
          const result = await Swal.fire({
            title: 'Êtes-vous sûr ?',
            text: "Cette action est irréversible !",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: 'var(--bank-danger)',
            cancelButtonColor: 'var(--bank-navy)',
            confirmButtonText: 'Oui, supprimer !',
            cancelButtonText: 'Annuler'
          });
  
          if (result.isConfirmed) {
            await axios.delete(`/api/actions-recommandations/${item.id}/`, { withCredentials: true, headers: { 'Content-Type': 'application/json' } });
  
            await Swal.fire(
              'Supprimé !',
              'La recommandation a été supprimée.',
              'success'
            );
  
            this.fetchRecommendations();
          }
        } catch (error) {
          console.error('Erreur lors de la suppression:', error);
          Swal.fire(
            'Erreur !',
            'Une erreur est survenue lors de la suppression.',
            'error'
          );
        }
      },
      formatDate(date) {
        return formatMandatDate(date);
      },
      formatStatut(statut) {
        return formatMandatStatut(statut);
      }
    }
  }
  </script>
  
  <style scoped>
  .table {
    min-width: 640px;
  }
  </style>
